import { ref, onBeforeUnmount } from 'vue';

/**
 * Composable for automatically closing a menu/dropdown when user scrolls beyond a threshold
 * @param {Object} options - Configuration options
 * @param {number} options.scrollThreshold - Pixels to scroll before closing (default: 100, use 0 for immediate)
 * @param {Function} options.onClose - Callback function to execute when menu should close
 * @returns {Object} - Methods to control the auto-close behavior
 */
export function useAutoCloseOnScroll(options = {}) {
    // Scroll threshold in pixels (default to 100 if not provided)
    const { scrollThreshold = 100, onClose = () => {} } = options;

    const isMenuOpen = ref(false);
    let initialScrollPosition = { x: 0, y: 0 };

    function handlePageScroll() {
        if (!isMenuOpen.value) return;

        // If threshold is 0, close immediately on any scroll
        if (scrollThreshold === 0) {
            closeMenu();
            return;
        }

        const currentScrollX =
            window.pageXOffset || document.documentElement.scrollLeft;
        const currentScrollY =
            window.pageYOffset || document.documentElement.scrollTop;

        const scrollDistanceX = Math.abs(currentScrollX - initialScrollPosition.x);
        const scrollDistanceY = Math.abs(currentScrollY - initialScrollPosition.y);

        // Close menu only if scroll distance exceeds threshold
        if (
            scrollDistanceX > scrollThreshold ||
            scrollDistanceY > scrollThreshold
        ) {
            closeMenu();
        }
    }

    function addScrollListener() {
        // Use capture phase for more comprehensive scroll detection
        window.addEventListener('scroll', handlePageScroll, { passive: true, capture: true });
        document.addEventListener('scroll', handlePageScroll, { passive: true, capture: true });
        // Also listen for wheel events to catch scrolling that might not trigger scroll events immediately
        window.addEventListener('wheel', handlePageScroll, { passive: true });
    }

    function removeScrollListener() {
        window.removeEventListener('scroll', handlePageScroll, { capture: true });
        document.removeEventListener('scroll', handlePageScroll, { capture: true });
        window.removeEventListener('wheel', handlePageScroll);
    }

    function openMenu() {
        // Capture initial scroll position when menu opens
        initialScrollPosition = {
            x: window.pageXOffset || document.documentElement.scrollLeft,
            y: window.pageYOffset || document.documentElement.scrollTop,
        };

        isMenuOpen.value = true;
        addScrollListener();
    }

    function closeMenu() {
        isMenuOpen.value = false;
        removeScrollListener();
        onClose();
    }

    function updateMenuState(isOpen) {
        if (isOpen) {
            openMenu();
        } else {
            closeMenu();
        }
    }

    onBeforeUnmount(() => {
        removeScrollListener();
    });

    return {
        isMenuOpen,
        openMenu,
        closeMenu,
        updateMenuState,
    };
}
